import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";

import {
  Alert,
  Box,
  CircularProgress,
  useTheme,
} from "@mui/material";

import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import StarIcon from "@mui/icons-material/Star";
import ReceiptIcon from "@mui/icons-material/Receipt";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import {
  getDeliveryCenterConfig,
  setDeliveryCenterConfig,
} from "../../../api/DeliveryCenter/deliveryCenter";
import {
  AnimatedModal,
  ModalContent,
  ModalHeader,
} from "../../modals/AnimatedModal";
import { dispatchSnackBarMessage } from "../../../utils/snackbar";
import ConfigSwitchCard from "../../styled/ConfigSwitchCard";

const configOptions = [
  {
    id: "require_confirmation_code_blacklisted_customers",
    title: "Código de Confirmação para Clientes em Alerta",
    description: {
      enabled:
        "Código de confirmação obrigatório para pedidos feitos por clientes em alerta. O entregador precisará informar um código antes de finalizar a entrega.",
      disabled:
        "Código de confirmação não será exigido para pedidos feitos por clientes em alerta. Entrega pode ser finalizada sem verificação adicional.",
    },
    icon: TrendingDownIcon,
    successMessage: (value) =>
      `Código de confirmação para pedidos baixos ${
        value ? "ativado" : "desativado"
      } com sucesso`,
    errorMessage:
      "Erro ao atualizar configuração de código para pedidos baixos",
    notificationId: "low-value-orders-confirmation-config",
  },
  {
    id: "require_confirmation_code_special_orders",
    title: "Código de Confirmação para Pedidos Especiais",
    description: {
      enabled:
        "Código de confirmação obrigatório para pedidos especiais (alto valor). O entregador precisará informar um código antes de finalizar a entrega.",
      disabled:
        "Código de confirmação não será exigido para pedidos especiais. Entrega pode ser finalizada sem verificação adicional.",
    },
    icon: StarIcon,
    successMessage: (value) =>
      `Código de confirmação para pedidos especiais ${
        value ? "ativado" : "desativado"
      } com sucesso`,
    errorMessage:
      "Erro ao atualizar configuração de código para pedidos especiais",
    notificationId: "special-orders-confirmation-config",
  },
  {
    id: "require_confirmation_code_normal_orders",
    title: "Código de Confirmação para Pedidos Normais",
    description: {
      enabled:
        "Código de confirmação obrigatório para pedidos normais. O entregador precisará informar um código antes de finalizar a entrega.",
      disabled:
        "Código de confirmação não será exigido para pedidos normais. Entrega pode ser finalizada sem verificação adicional.",
    },
    icon: ReceiptIcon,
    successMessage: (value) =>
      `Código de confirmação para pedidos normais ${
        value ? "ativado" : "desativado"
      } com sucesso`,
    errorMessage:
      "Erro ao atualizar configuração de código para pedidos normais",
    notificationId: "normal-orders-confirmation-config",
  },
  {
    id: "require_confirmation_code_low_value_orders",
    title: "Código de Confirmação para Pedidos Baixos",
    description: {
      enabled:
        "Código de confirmação obrigatório para pedidos de baixo valor. O entregador precisará informar um código antes de finalizar a entrega.",
      disabled:
        "Código de confirmação não será exigido para pedidos de baixo valor. Entrega pode ser finalizada sem verificação adicional.",
    },
    icon: TrendingDownIcon,
    successMessage: (value) =>
      `Código de confirmação para pedidos baixos ${
        value ? "ativado" : "desativado"
      } com sucesso`,
    errorMessage:
      "Erro ao atualizar configuração de código para pedidos baixos",
    notificationId: "low-value-orders-confirmation-config",
  },
];

export default function DeliveryConfirmationConfigsModal({ show, closeModal }) {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showError, setShowError] = useState(false);
  const [configValues, setConfigValues] = useState({});

  useEffect(() => {
    if (!show) return;
    loadConfigs();
  }, [show]);

  const loadConfigs = async () => {
    try {
      setLoading(true);
      setErrorMessage("");
      setShowError(false);

      const configParams = configOptions.map((option) => option.id).join(",");
      const params = { config: configParams };
      const { data } = await getDeliveryCenterConfig(params);

      setConfigValues(data || {});
    } catch (err) {
      console.error(err);
      setErrorMessage(
        "Ocorreu um erro ao carregar as configurações. Tente novamente."
      );
      setShowError(true);
      dispatchSnackBarMessage(
        "configs-load-error",
        "Erro ao carregar configurações",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleConfigChange = async (configId, event) => {
    const isChecked = event.target.checked;
    const configOption = configOptions.find((option) => option.id === configId);

    if (!configOption) return;

    const newValueForApi = isChecked;
    const successMsg = configOption.successMessage(newValueForApi);

    try {
      await setDeliveryCenterConfig({
        config: { [configId]: newValueForApi },
      });

      setConfigValues((prev) => ({
        ...prev,
        [configId]: newValueForApi,
      }));

      dispatchSnackBarMessage(
        configOption.notificationId,
        successMsg,
        "success"
      );
    } catch (e) {
      console.error(e);
      setConfigValues((prev) => ({
        ...prev,
        [configId]: configValues[configId],
      }));
      dispatchSnackBarMessage(
        `${configOption.notificationId}-error`,
        configOption.errorMessage,
        "error"
      );
    }
  };

  return (
    <AnimatedModal
      isOpen={show}
      onClose={closeModal}
      width="600px"
      maxWidth="90%"
    >
      <ModalHeader
        title="Configurações de confirmação de entrega"
        icon={LocalShippingIcon}
        onClose={closeModal}
      />
      <ModalContent>
        <Box sx={{ px: 3, pt: 2, pb: 4 }}>
          <AnimatePresence>
            {showError && (
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <Alert
                  severity="error"
                  onClose={() => setShowError(false)}
                  sx={{ mb: 3, borderRadius: 2 }}
                >
                  {errorMessage}
                </Alert>
              </motion.div>
            )}
          </AnimatePresence>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            configOptions.map((configOpt) => {
              const isActive = !!configValues[configOpt.id];

              return (
                <ConfigSwitchCard
                  key={configOpt.id}
                  icon={configOpt.icon}
                  title={configOpt.title}
                  description={
                    isActive
                      ? configOpt.description.enabled
                      : configOpt.description.disabled
                  }
                  isActive={isActive}
                  onChange={(e) => handleConfigChange(configOpt.id, e)}
                />
              );
            })
          )}
        </Box>
      </ModalContent>
    </AnimatedModal>
  );
}
